import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Pagination, Paper, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { DashboardContent } from '../Business/modules/Dashboard'
import { TransactionsContent } from '../Business/modules/Transactions'
import { periodQuery, useReport, PeriodFilter, ReportState, ReportRefresh, Metric, MetricGrid } from '../Business/components/Reporting'
import { timestamp } from './shared'

export default function Metrics({ kind, id, period, onPeriodChange, view, onViewChange }) {
  if (kind === 'users') return <UserMetrics id={id} period={period} onPeriodChange={onPeriodChange} />
  return <Stack spacing={2.5}>
    <Tabs sx={{ '& .MuiTab-root': { textTransform: 'none' } }} value={view} onChange={(_, value) => onViewChange(value)} aria-label="Business metrics views">
      <Tab value="dashboard" label="Dashboard" id="dashboard-tab" aria-controls="dashboard-panel" />
      <Tab value="transactions" label="Transactions" id="transactions-tab" aria-controls="transactions-panel" />
    </Tabs>
    <Box role="tabpanel" id={`${view}-panel`} aria-labelledby={`${view}-tab`}>
      {view === 'dashboard'
        ? <DashboardContent businessId={id} period={period} onPeriodChange={onPeriodChange} admin />
        : <TransactionsContent businessId={id} period={period} onPeriodChange={onPeriodChange} admin />}
    </Box>
  </Stack>
}

function UserMetrics({ id, period, onPeriodChange }) {
  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [id, period.from, period.to])
  const dates = periodQuery(period)
  const summary = useReport(`/admin/users/${id}/metrics`, dates)
  const claims = useReport(`/admin/users/${id}/claims`, dates && { ...dates, page, size: 20 })
  const data = summary.data
  const list = claims.data
  const count = value => value?.toLocaleString('en-US')
  return <Stack spacing={2.5}>
    <Typography color="text.secondary">Claims are recorded claim debits attributed to this account. Repeat code views without a debit are excluded.</Typography>
    <ReportRefresh reports={[summary, claims]} />
    <PeriodFilter period={period} onChange={value => { setPage(0); onPeriodChange(value) }} />
    <ReportState report={summary} label="Metrics" />
    {dates && <>
      <Metric label="All-time claims" value={count(data?.allTimeClaims)} note="All time · Unaffected by date filters" />
      <MetricGrid>
        <Metric label="Claims" value={count(data?.claims)} />
        <Metric label="Offers claimed" value={count(data?.offers)} note="Distinct offers in the selected period" />
        <Metric label="Businesses claimed from" value={count(data?.businesses)} note="Distinct businesses in the selected period" />
        <Metric label="Last claim" value={data ? (data.lastClaimAt ? timestamp(data.lastClaimAt) : 'No claims in this period') : undefined} note="Within the selected period" />
      </MetricGrid>
    </>}
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box p={2.5}><Typography fontWeight={600}>Claim history</Typography><Typography variant="caption" color="text.secondary">Recorded claims in the selected period, newest first.</Typography></Box>
      <ReportState report={claims} label="Claim history" />
      {!dates && <Typography p={2.5} color="text.secondary">Choose a valid period to view claims.</Typography>}
      {list && <>
        <TableContainer><Table aria-label="User claim history" sx={{ minWidth: 540, '& th': { bgcolor: 'grey.50', color: 'text.secondary' } }}>
          <TableHead><TableRow>{['Claimed at', 'Offer', 'Business'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{list.content.map(claim => <TableRow hover key={`${claim.transactionId}:${claim.sessionId}`}>
            <TableCell>{timestamp(claim.claimedAt)}</TableCell>
            <TableCell>{claim.offerTitle || `Offer ${claim.offerId}`}</TableCell>
            <TableCell><Link to={`/admin/businesses/${claim.businessId}`}>{claim.businessName || `Business ${claim.businessId}`}</Link></TableCell>
          </TableRow>)}{!list.content.length && <TableRow><TableCell colSpan={3} align="center"><Box py={4}>No claims in this period.</Box></TableCell></TableRow>}</TableBody>
        </Table></TableContainer>
        <Stack p={2.5} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">{list.totalElements ? `${page * 20 + 1}–${Math.min((page + 1) * 20, list.totalElements)} of ${list.totalElements}` : '0 claims'}</Typography>
          <Pagination page={page + 1} count={Math.max(1, list.totalPages)} onChange={(_, value) => setPage(value - 1)} color="primary" />
        </Stack>
      </>}
    </Paper>
  </Stack>
}
